import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import ModernTableCard from '../components/ModernTableCard';
import { 
    MapPin, Users, Clock, ShoppingCart, CheckCircle, 
    RefreshCcw, Plus, Eye, Bell, ChefHat, Utensils,
    AlertTriangle, Search, X, Check, CreditCard,
    Timer, ArrowRight, Coffee, Send, MessageSquare,
    Receipt, UserPlus, Minus, Loader2, Edit, FileText,
    Lock, Unlock, Monitor, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// ========================================
// UTILITY FUNCTIONS
// ========================================

const formatDuration = (minutes) => {
    if (!minutes || minutes < 1) return '-';
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
};

const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('fr-FR', {
        timeZone: 'Africa/Libreville',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// ========================================
// STAT CARD - Large touch-friendly
// ========================================

const StatCard = ({ title, value, subtitle, icon: Icon, status = 'default', onClick, large }) => {
    const colors = {
        green: 'bg-green-100 border-green-300 text-green-700',
        orange: 'bg-orange-100 border-orange-300 text-orange-700',
        red: 'bg-red-100 border-red-300 text-red-700',
        blue: 'bg-blue-100 border-blue-300 text-blue-700',
        purple: 'bg-purple-100 border-purple-300 text-purple-700',
        default: 'bg-white border-gray-200 text-gray-700'
    };
    
    return (
        <div 
            className={cn(
                "p-4 rounded-2xl border-2 transition-all active:scale-95",
                colors[status],
                onClick && "cursor-pointer",
                large && "p-6"
            )}
            onClick={onClick}
        >
            <div className="flex items-center justify-between gap-2">
                <div>
                    <p className={cn("font-medium opacity-70", large ? "text-sm" : "text-xs")}>{title}</p>
                    <p className={cn("font-bold", large ? "text-4xl" : "text-2xl")}>{value}</p>
                    {subtitle && <p className="text-xs opacity-60 mt-1">{subtitle}</p>}
                </div>
                {Icon && (
                    <div className={cn(
                        "rounded-xl flex items-center justify-center",
                        large ? "w-14 h-14" : "w-10 h-10",
                        status === 'green' && "bg-green-200",
                        status === 'orange' && "bg-orange-200",
                        status === 'red' && "bg-red-200",
                        status === 'blue' && "bg-blue-200",
                        status === 'purple' && "bg-purple-200",
                        status === 'default' && "bg-gray-100"
                    )}>
                        <Icon className={cn(large ? "w-7 h-7" : "w-5 h-5")} />
                    </div>
                )}
            </div>
        </div>
    );
};

// ========================================
// TABLE CARD - Large touch-friendly card
// ========================================

// ========================================
// TABLE CARD - Now using ModernTableCard component
// ========================================
// NOTE: TableCard has been replaced with ModernTableCard for consistent styling
// The local TableCard component is kept as a wrapper for backward compatibility

const TableCardWrapper = ({ table, onClick, isSelected, currentUser }) => (
    <ModernTableCard
        table={table}
        onClick={onClick}
        isSelected={isSelected}
        size="normal"
        currentUser={currentUser}
    />
);

// ========================================
// ORDER CARD - Ready to serve notification
// ========================================

const OrderReadyCard = ({ order, onServed }) => {
    return (
        <div className="p-4 rounded-2xl bg-green-100 border-2 border-green-400 animate-pulse">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-green-700" />
                    <span className="font-bold text-green-700">PRÊT À SERVIR</span>
                </div>
                <Badge className="bg-green-600">Table {order.table_number}</Badge>
            </div>
            <div className="text-sm text-green-700 mb-3">
                {order.items?.map((item, i) => (
                    <span key={i}>
                        {i > 0 && ', '}
                        {item.quantity}x {item.name}
                    </span>
                ))}
            </div>
            <Button 
                className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
                onClick={() => onServed?.(order)}
            >
                <Check className="w-5 h-5 mr-2" />
                Servi
            </Button>
        </div>
    );
};

// ========================================
// KITCHEN ORDER STATUS
// ========================================

const KitchenOrderStatus = ({ order }) => {
    const isReady = order.status === 'ready';
    const isPreparing = order.status === 'preparing';
    const waitTime = order.wait_time || 0;
    const isLate = waitTime > 15;
    
    return (
        <div className={cn(
            "p-3 rounded-xl border-2",
            isReady && "bg-green-50 border-green-300",
            isPreparing && "bg-orange-50 border-orange-300",
            !isReady && !isPreparing && "bg-blue-50 border-blue-300",
            isLate && !isReady && "animate-pulse"
        )}>
            <div className="flex items-center justify-between mb-1">
                <Badge variant={isLate ? "destructive" : "outline"} className="text-xs">
                    Table {order.table_number}
                </Badge>
                <span className={cn(
                    "text-xs font-medium",
                    isLate ? "text-red-600" : "text-muted-foreground"
                )}>
                    {waitTime} min
                </span>
            </div>
            <div className="text-xs text-muted-foreground">
                {order.items?.slice(0, 2).map((item, i) => (
                    <span key={i}>{i > 0 && ' • '}{item.name}</span>
                ))}
            </div>
            <div className="mt-2 flex items-center gap-1">
                {isReady ? (
                    <Badge className="bg-green-600 text-xs">Prêt</Badge>
                ) : isPreparing ? (
                    <Badge className="bg-orange-500 text-xs">En préparation</Badge>
                ) : (
                    <Badge variant="outline" className="text-xs">En attente</Badge>
                )}
            </div>
        </div>
    );
};

// ========================================
// PRODUCT BUTTON - Quick add
// ========================================

const ProductButton = ({ product, onAdd, disabled }) => {
    const categoryColors = {
        'Entrées': 'bg-amber-100 border-amber-300 hover:bg-amber-200',
        'Plats': 'bg-blue-100 border-blue-300 hover:bg-blue-200',
        'Desserts': 'bg-pink-100 border-pink-300 hover:bg-pink-200',
        'Boissons': 'bg-cyan-100 border-cyan-300 hover:bg-cyan-200',
        'Accompagnements': 'bg-green-100 border-green-300 hover:bg-green-200',
        'default': 'bg-gray-100 border-gray-300 hover:bg-gray-200'
    };
    
    const colorClass = categoryColors[product.category] || categoryColors.default;
    
    return (
        <button
            className={cn(
                "p-3 rounded-xl border-2 text-left transition-all active:scale-95",
                colorClass,
                disabled && "opacity-50 cursor-not-allowed",
                product.stock === 0 && "opacity-30 line-through"
            )}
            onClick={() => !disabled && product.stock !== 0 && onAdd?.(product)}
            disabled={disabled || product.stock === 0}
        >
            <div className="font-medium text-sm truncate">{product.name}</div>
            <div className="text-xs text-muted-foreground">{product.price?.toLocaleString()} F</div>
            {product.stock === 0 && (
                <Badge variant="destructive" className="text-[10px] mt-1">Rupture</Badge>
            )}
        </button>
    );
};

// ========================================
// MAIN SERVER DASHBOARD
// ========================================

const ServerDashboardPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === 'ADMIN';
    
    // State
    const [loading, setLoading] = useState(true);
    const [tables, setTables] = useState([]);
    const [myTables, setMyTables] = useState([]);
    const [kitchenOrders, setKitchenOrders] = useState([]);
    const [readyOrders, setReadyOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    
    // Selected table for ordering
    const [selectedTable, setSelectedTable] = useState(null);
    const [orderDialog, setOrderDialog] = useState(false);
    const [cart, setCart] = useState([]);
    const [kitchenNote, setKitchenNote] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sendingOrder, setSendingOrder] = useState(false);
    
    // Order view (consultation)
    const [orderViewOpen, setOrderViewOpen] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);
    const [loadingOrder, setLoadingOrder] = useState(false);
    
    // Open table dialog
    const [openTableDialog, setOpenTableDialog] = useState(false);
    const [newTableCovers, setNewTableCovers] = useState(2);
    
    // Cash register session state
    const [registerStatus, setRegisterStatus] = useState(null);
    const [loadingRegister, setLoadingRegister] = useState(true);
    const [openSessionDialog, setOpenSessionDialog] = useState(false);
    const [pinCode, setPinCode] = useState('');
    const [openingCash, setOpeningCash] = useState('0');
    const [openingSession, setOpeningSession] = useState(false);
    
    const intervalRef = useRef(null);
    
    // ========================================
    // DATA FETCHING
    // ========================================
    
    const fetchData = useCallback(async (showToast = false) => {
        try {
            const [tablesRes, kitchenRes, productsRes, categoriesRes] = await Promise.all([
                api().get('/tables').catch(() => ({ data: [] })),
                api().get('/kitchen/tickets').catch(() => ({ data: [] })),
                api().get('/products?in_stock=true').catch(() => ({ data: [] })),
                api().get('/categories').catch(() => ({ data: [] }))
            ]);
            
            const allTables = tablesRes.data || [];
            setTables(allTables);
            
            // Filter tables assigned to current user (or all if admin/directeur)
            const isManager = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);
            const filtered = isManager ? allTables : allTables.filter(t => 
                t.server_id === user?.id || t.assigned_to === user?.id || !t.server_id
            );
            setMyTables(filtered);
            
            const orders = kitchenRes.data || [];
            setKitchenOrders(orders);
            setReadyOrders(orders.filter(o => o.status === 'ready'));
            
            setProducts(productsRes.data || []);
            setCategories(categoriesRes.data || []);
            
            setLastUpdate(new Date());
            
            if (showToast) {
                toast.success('Données actualisées');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [api, user]);
    
    // Fetch cash register status
    const fetchRegisterStatus = useCallback(async () => {
        try {
            const res = await api().get('/pos/my-register-status');
            setRegisterStatus(res.data);
        } catch (error) {
            console.error('Error fetching register status:', error);
            setRegisterStatus(null);
        } finally {
            setLoadingRegister(false);
        }
    }, [api]);
    
    // Open own session with PIN (ADMIN bypasses PIN)
    const handleOpenMySession = async () => {
        // ADMIN can open without PIN - full access
        if (!isAdmin && (!pinCode || pinCode.length < 4)) {
            toast.error('Veuillez entrer votre code PIN');
            return;
        }
        
        setOpeningSession(true);
        try {
            const res = await api().post('/pos/my-session/open', {
                pin_code: isAdmin ? '0000' : pinCode,  // ADMIN sends dummy PIN, backend will bypass
                opening_cash: parseFloat(openingCash) || 0
            });
            
            if (res.data.success) {
                toast.success(res.data.message || 'Session ouverte avec succès !');
                setOpenSessionDialog(false);
                setPinCode('');
                setOpeningCash('0');
                // Refresh register status
                await fetchRegisterStatus();
            }
        } catch (error) {
            console.error('Error opening session:', error);
            const message = error.response?.data?.detail || 'Erreur lors de l\'ouverture de la session';
            toast.error(message);
        } finally {
            setOpeningSession(false);
        }
    };
    
    // Initial load
    useEffect(() => {
        fetchData();
        fetchRegisterStatus();
    }, []);
    
    // Auto-refresh every 5 seconds
    useEffect(() => {
        if (autoRefresh) {
            intervalRef.current = setInterval(() => fetchData(false), 5000);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoRefresh, fetchData]);
    
    // ========================================
    // COMPUTED VALUES
    // ========================================
    
    const tablesOccupied = myTables.filter(t => (t.status === 'occupied' || t.status === 'occupee')).length;
    const tablesAvailable = myTables.filter(t => t.status === 'available').length;
    const tablesWaiting = myTables.filter(t => t.status === 'waiting_payment').length;
    const myOrders = kitchenOrders.filter(o => 
        myTables.some(t => t.id === o.table_id || t.number === o.table_number)
    );
    const myOrdersInProgress = myOrders.filter(o => ['pending', 'preparing'].includes(o.status)).length;
    const myOrdersReady = myOrders.filter(o => o.status === 'ready').length;
    
    // Filter products
    const filteredProducts = products.filter(p => {
        const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
        const matchesSearch = !searchQuery || 
            p.name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });
    
    // Cart total
    const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // ========================================
    // HANDLERS
    // ========================================
    
    const handleTableClick = async (table) => {
        setSelectedTable(table);
        
        if (table.status === 'available') {
            // Open table dialog
            setNewTableCovers(2);
            setOpenTableDialog(true);
        } else {
            // Show order consultation first
            setLoadingOrder(true);
            setOrderViewOpen(true);
            try {
                const response = await api().get(`/pos/table/${table.number}/current-order`);
                setOrderDetails(response.data);
            } catch (error) {
                console.error('Erreur chargement commande:', error);
                setOrderDetails(null);
            } finally {
                setLoadingOrder(false);
            }
        }
    };

    // Handler for quick actions from ModernTableCard
    const handleQuickAction = async (action, table) => {
        setSelectedTable(table);
        
        switch (action) {
            case 'add':
                // Open order dialog for adding products
                setLoadingOrder(true);
                try {
                    const response = await api().get(`/pos/table/${table.number}/current-order`);
                    setOrderDetails(response.data);
                } catch (error) {
                    setOrderDetails(null);
                } finally {
                    setLoadingOrder(false);
                }
                setOrderViewOpen(false);
                setCart([]);
                setKitchenNote('');
                setOrderDialog(true);
                break;
            case 'view':
                setLoadingOrder(true);
                setOrderViewOpen(true);
                try {
                    const response = await api().get(`/pos/table/${table.number}/current-order`);
                    setOrderDetails(response.data);
                } catch (error) {
                    setOrderDetails(null);
                } finally {
                    setLoadingOrder(false);
                }
                break;
            case 'pay':
                navigate(`/pos?table=${table?.number || table?.id}&action=payment`);
                break;
            case 'call':
                try {
                    await api().post('/service-notifications/call-manager', {
                        type: 'call_manager',
                        message: `Appel manager - Table ${table.number || table.name}`,
                        table_id: String(table.id),
                        table_number: String(table.number || table.name),
                        priority: 'high'
                    });
                    toast.success(`Manager appelé pour la table ${table.number || table.name}`);
                } catch (error) {
                    toast.error('Erreur lors de l\'appel du manager');
                }
                break;
            default:
                handleTableClick(table);
        }
    };

    const openOrderEditDialog = () => {
        setOrderViewOpen(false);
        setCart([]);
        setKitchenNote('');
        setOrderDialog(true);
    };
    
    const handleOpenTable = async () => {
        if (!selectedTable) return;
        
        try {
            await api().post(`/tables/${selectedTable.id}/open`, {
                covers: newTableCovers
            });
            toast.success(`Table ${selectedTable.number || selectedTable.name} ouverte`);
            setOpenTableDialog(false);
            fetchData();
            
            // Open order dialog
            setCart([]);
            setKitchenNote('');
            setOrderDialog(true);
        } catch (error) {
            toast.error('Erreur lors de l\'ouverture');
        }
    };
    
    const handleAddToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item => 
                    item.id === product.id 
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
    };
    
    const handleUpdateQuantity = (productId, delta) => {
        setCart(prev => {
            return prev.map(item => {
                if (item.id === productId) {
                    const newQty = item.quantity + delta;
                    return newQty > 0 ? { ...item, quantity: newQty } : null;
                }
                return item;
            }).filter(Boolean);
        });
    };
    
    const handleRemoveFromCart = (productId) => {
        setCart(prev => prev.filter(item => item.id !== productId));
    };
    
    const handleSendToKitchen = async () => {
        if (cart.length === 0 || !selectedTable) return;
        
        setSendingOrder(true);
        try {
            await api().post('/pos/orders', {
                table_id: selectedTable.id,
                items: cart.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    price: item.price,
                    name: item.name
                })),
                kitchen_note: kitchenNote || undefined,
                send_to_kitchen: true
            });
            
            toast.success('Commande envoyée en cuisine');
            setCart([]);
            setKitchenNote('');
            setOrderDialog(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setSendingOrder(false);
        }
    };
    
    const handleOrderServed = async (order) => {
        try {
            await api().put(`/kitchen/tickets/${order.id}`, { status: 'servi' });
            toast.success('Commande servie');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    const handleRequestBill = async () => {
        if (!selectedTable) return;
        
        try {
            await api().put(`/tables/${selectedTable.id}/status`, { status: 'waiting_payment' });
            toast.success('Addition demandée');
            setOrderDialog(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    // ========================================
    // RENDER
    // ========================================
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <RefreshCcw className="w-8 h-8 animate-spin text-orange-600" />
                <span className="ml-3 text-muted-foreground">Chargement...</span>
            </div>
        );
    }

    return (
        <div className="p-2 sm:p-4 space-y-4 max-w-[1200px] mx-auto" data-testid="server-dashboard">
            {/* ========================================
                HEADER
            ======================================== */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                        <Utensils className="w-6 h-6 text-orange-600" />
                        Mes Tables
                    </h1>
                    <p className="text-xs text-muted-foreground">
                        {lastUpdate && formatTime(lastUpdate.toISOString())}
                    </p>
                </div>
                <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchData(true)}
                    className="h-10 px-4"
                >
                    <RefreshCcw className={cn("w-4 h-4 mr-1", loading && "animate-spin")} />
                    Actualiser
                </Button>
            </div>

            {/* ========================================
                CASH REGISTER STATUS
            ======================================== */}
            {!loadingRegister && (
                <div className={cn(
                    "p-4 rounded-2xl border-2",
                    registerStatus?.has_active_session 
                        ? "bg-green-50 border-green-300" 
                        : registerStatus?.has_register 
                            ? "bg-amber-50 border-amber-300"
                            : "bg-red-50 border-red-300"
                )}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {registerStatus?.has_active_session ? (
                                <div className="p-2 bg-green-200 rounded-full">
                                    <Unlock className="w-5 h-5 text-green-700" />
                                </div>
                            ) : registerStatus?.has_register ? (
                                <div className="p-2 bg-amber-200 rounded-full">
                                    <Lock className="w-5 h-5 text-amber-700" />
                                </div>
                            ) : (
                                <div className="p-2 bg-red-200 rounded-full">
                                    <AlertTriangle className="w-5 h-5 text-red-700" />
                                </div>
                            )}
                            <div>
                                <p className={cn(
                                    "font-semibold",
                                    registerStatus?.has_active_session ? "text-green-700" :
                                    registerStatus?.has_register ? "text-amber-700" : "text-red-700"
                                )}>
                                    {registerStatus?.has_active_session 
                                        ? `Caisse active : ${registerStatus.session?.register_name || 'Ma caisse'}`
                                        : registerStatus?.has_register 
                                            ? `Caisse assignée : ${registerStatus.register?.name || 'En attente d\'ouverture'}`
                                            : "Aucune caisse assignée"
                                    }
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {registerStatus?.has_active_session 
                                        ? `Ouverte à ${formatTime(registerStatus.session?.opened_at)}`
                                        : registerStatus?.has_register 
                                            ? "Session non ouverte - Cliquez pour ouvrir"
                                            : "Contactez la direction pour obtenir une caisse"
                                    }
                                </p>
                            </div>
                        </div>
                        
                        {/* Button to open session */}
                        {registerStatus?.has_register && !registerStatus?.has_active_session && (
                            <Button 
                                onClick={() => setOpenSessionDialog(true)}
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid="open-session-btn"
                            >
                                <KeyRound className="w-4 h-4 mr-2" />
                                Ouvrir ma session
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* ========================================
                READY ORDERS NOTIFICATIONS
            ======================================== */}
            {myOrdersReady > 0 && (
                <div className="space-y-2">
                    <h2 className="font-bold text-green-700 flex items-center gap-2">
                        <Bell className="w-5 h-5 animate-bounce" />
                        Commandes prêtes à servir ({myOrdersReady})
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {myOrders.filter(o => o.status === 'ready').map(order => (
                            <OrderReadyCard 
                                key={order.id} 
                                order={order} 
                                onServed={handleOrderServed}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* ========================================
                STATS ROW
            ======================================== */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <StatCard
                    title="Mes tables"
                    value={myTables.length}
                    icon={MapPin}
                    status="blue"
                />
                <StatCard
                    title="Occupées"
                    value={tablesOccupied}
                    icon={Users}
                    status={tablesOccupied > 0 ? "blue" : "default"}
                />
                <StatCard
                    title="Libres"
                    value={tablesAvailable}
                    icon={Check}
                    status="green"
                />
                <StatCard
                    title="En cuisine"
                    value={myOrdersInProgress}
                    icon={ChefHat}
                    status={myOrdersInProgress > 0 ? "orange" : "default"}
                />
                <StatCard
                    title="Prêtes"
                    value={myOrdersReady}
                    icon={Bell}
                    status={myOrdersReady > 0 ? "green" : "default"}
                />
            </div>

            {/* ========================================
                TABLES GRID
            ======================================== */}
            <div>
                <h2 className="font-bold mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    Mes Tables ({myTables.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {myTables.map(table => (
                        <ModernTableCard
                            key={table.id}
                            table={table}
                            onClick={handleTableClick}
                            onQuickAction={handleQuickAction}
                            isSelected={selectedTable?.id === table.id}
                            size="normal"
                            currentUser={user}
                        />
                    ))}
                </div>
                {myTables.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                        <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucune table attribuée</p>
                    </div>
                )}
            </div>

            {/* ========================================
                KITCHEN ORDERS TRACKING
            ======================================== */}
            {myOrdersInProgress > 0 && (
                <div>
                    <h2 className="font-bold mb-3 flex items-center gap-2">
                        <ChefHat className="w-5 h-5 text-orange-600" />
                        En cuisine ({myOrdersInProgress})
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {myOrders.filter(o => ['pending', 'preparing'].includes(o.status)).map(order => (
                            <KitchenOrderStatus key={order.id} order={order} />
                        ))}
                    </div>
                </div>
            )}

            {/* ========================================
                QUICK ACTIONS
            ======================================== */}
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-4 py-2 shadow-xl border z-50">
                <Button 
                    size="lg" 
                    className="rounded-full bg-orange-600 hover:bg-orange-700 h-12 px-6"
                    onClick={() => navigate('/pos')}
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nouvelle commande
                </Button>
                <Button 
                    size="lg" 
                    variant="outline"
                    className="rounded-full h-12"
                    onClick={() => navigate('/kitchen-dashboard')}
                >
                    <ChefHat className="w-5 h-5 mr-1" />
                    Cuisine
                </Button>
            </div>
            
            {/* Bottom padding */}
            <div className="h-20" />

            {/* ========================================
                DIALOG: Open Table
            ======================================== */}
            <Dialog open={openTableDialog} onOpenChange={setOpenTableDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            Ouvrir Table {selectedTable?.number || selectedTable?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-6">
                        <label className="text-sm font-medium mb-2 block">Nombre de clients</label>
                        <div className="flex items-center justify-center gap-4">
                            <Button
                                variant="outline"
                                size="lg"
                                className="h-14 w-14 rounded-full"
                                onClick={() => setNewTableCovers(Math.max(1, newTableCovers - 1))}
                            >
                                <Minus className="w-6 h-6" />
                            </Button>
                            <span className="text-5xl font-bold w-20 text-center">{newTableCovers}</span>
                            <Button
                                variant="outline"
                                size="lg"
                                className="h-14 w-14 rounded-full"
                                onClick={() => setNewTableCovers(newTableCovers + 1)}
                            >
                                <Plus className="w-6 h-6" />
                            </Button>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setOpenTableDialog(false)} className="flex-1 h-12">
                            Annuler
                        </Button>
                        <Button onClick={handleOpenTable} className="flex-1 h-12 bg-orange-600 hover:bg-orange-700">
                            <UserPlus className="w-5 h-5 mr-2" />
                            Ouvrir
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========================================
                DIALOG: Order/Manage Table
            ======================================== */}
            <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center justify-between">
                            <span>Table {selectedTable?.number || selectedTable?.name}</span>
                            {selectedTable?.covers > 0 && (
                                <Badge variant="outline">{selectedTable.covers} client(s)</Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* LEFT: Products */}
                        <div className="flex flex-col overflow-hidden">
                            {/* Search */}
                            <div className="relative mb-3">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher un produit..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 h-11"
                                />
                            </div>
                            
                            {/* Categories */}
                            <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                                <Button
                                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setSelectedCategory('all')}
                                    className="shrink-0"
                                >
                                    Tous
                                </Button>
                                {categories.map(cat => (
                                    <Button
                                        key={cat.id}
                                        variant={selectedCategory === cat.name ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setSelectedCategory(cat.name)}
                                        className="shrink-0"
                                    >
                                        {cat.name}
                                    </Button>
                                ))}
                            </div>
                            
                            {/* Products grid */}
                            <ScrollArea className="flex-1">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pr-2">
                                    {filteredProducts.map(product => (
                                        <ProductButton
                                            key={product.id}
                                            product={product}
                                            onAdd={handleAddToCart}
                                        />
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                        
                        {/* RIGHT: Cart */}
                        <div className="flex flex-col bg-gray-50 rounded-xl p-3">
                            <h3 className="font-bold mb-3 flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5" />
                                Commande ({cart.length})
                            </h3>
                            
                            <ScrollArea className="flex-1 mb-3">
                                {cart.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                        <p className="text-sm">Panier vide</p>
                                        <p className="text-xs">Sélectionnez des produits</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {cart.map(item => (
                                            <div key={item.id} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium truncate">{item.name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {item.price?.toLocaleString()} F
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => handleUpdateQuantity(item.id, -1)}
                                                    >
                                                        <Minus className="w-3 h-3" />
                                                    </Button>
                                                    <span className="w-8 text-center font-bold">{item.quantity}</span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => handleUpdateQuantity(item.id, 1)}
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-red-500"
                                                    onClick={() => handleRemoveFromCart(item.id)}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </ScrollArea>
                            
                            {/* Kitchen note */}
                            <div className="mb-3">
                                <Textarea
                                    placeholder="Note pour la cuisine..."
                                    value={kitchenNote}
                                    onChange={(e) => setKitchenNote(e.target.value)}
                                    className="h-16 resize-none"
                                />
                            </div>
                            
                            {/* Total */}
                            <div className="p-3 bg-white rounded-lg mb-3">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">Total</span>
                                    <span className="text-xl font-bold">{cartTotal.toLocaleString()} F</span>
                                </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    variant="outline"
                                    className="h-12"
                                    onClick={handleRequestBill}
                                    disabled={selectedTable?.status === 'available'}
                                >
                                    <Receipt className="w-4 h-4 mr-2" />
                                    Addition
                                </Button>
                                <Button
                                    className="h-12 bg-orange-600 hover:bg-orange-700"
                                    onClick={handleSendToKitchen}
                                    disabled={cart.length === 0 || sendingOrder}
                                >
                                    {sendingOrder ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4 mr-2" />
                                    )}
                                    Envoyer
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog: Order View (Consultation) */}
            <Dialog open={orderViewOpen} onOpenChange={setOrderViewOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-teal-600" />
                            Table {selectedTable?.name || selectedTable?.number}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedTable?.status === 'a_debarrasser' ? 'Commande payée' : 'Commande en cours'}
                        </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="flex-1 pr-4">
                        {loadingOrder ? (
                            <div className="flex items-center justify-center py-12">
                                <RefreshCcw className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : orderDetails?.has_order ? (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                                    <div>
                                        <p className="text-xs text-muted-foreground">Commande</p>
                                        <p className="font-bold">#{orderDetails.order?.order_number?.slice(-4) || orderDetails.order?.id?.slice(-4)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Cuisine</p>
                                        <Badge variant="outline" className={
                                            orderDetails.order?.kitchen_status === 'pret' ? 'bg-green-50 text-green-700' :
                                            orderDetails.order?.kitchen_status === 'en_preparation' ? 'bg-amber-50 text-amber-700' :
                                            'bg-blue-50 text-blue-700'
                                        }>
                                            {orderDetails.order?.kitchen_status || 'En attente'}
                                        </Badge>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-semibold mb-2 text-sm">Articles ({orderDetails.items?.length || 0})</h4>
                                    <div className="space-y-2">
                                        {orderDetails.items?.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="w-6 h-6 flex items-center justify-center text-xs">
                                                        {item.quantity || 1}
                                                    </Badge>
                                                    <span className="text-sm font-medium">{item.name || item.product_name}</span>
                                                </div>
                                                <span className="text-sm font-semibold">
                                                    {(item.subtotal || item.total || ((item.unit_price || item.price || 0) * (item.quantity || 1))).toLocaleString()} F
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="border-t pt-3">
                                    <div className="flex items-center justify-between text-lg font-bold">
                                        <span>Total</span>
                                        <span className="text-teal-600">{(orderDetails.total || 0).toLocaleString()} F</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                <p>Aucune commande</p>
                            </div>
                        )}
                    </ScrollArea>
                    <DialogFooter className="border-t pt-4 gap-2 flex-col sm:flex-row">
                        <Button variant="outline" onClick={() => setOrderViewOpen(false)} className="w-full sm:w-auto">
                            Fermer
                        </Button>
                        <Button onClick={openOrderEditDialog} className="w-full sm:flex-1 bg-teal-600 hover:bg-teal-700">
                            <Edit className="w-4 h-4 mr-2" />
                            {orderDetails?.has_order ? 'Ajouter des articles' : 'Nouvelle commande'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========================================
                DIALOG: Open Session with PIN
            ======================================== */}
            <Dialog open={openSessionDialog} onOpenChange={setOpenSessionDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-amber-600" />
                            Ouvrir ma session
                        </DialogTitle>
                        <DialogDescription>
                            {isAdmin ? 'Accès administrateur - pas de PIN requis' : 'Entrez votre code PIN pour ouvrir votre caisse'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        {/* PIN Code Input - Hidden for ADMIN */}
                        {!isAdmin && (
                            <div>
                                <label className="text-sm font-medium mb-2 block">Code PIN</label>
                                <Input
                                    type="password"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="Entrez votre PIN"
                                    value={pinCode}
                                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="h-14 text-center text-2xl tracking-widest font-mono"
                                    data-testid="pin-code-input"
                                    autoFocus
                                />
                            </div>
                        )}
                        
                        {/* ADMIN Access Badge */}
                        {isAdmin && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                                <p className="text-green-700 font-medium">Accès administrateur</p>
                                <p className="text-sm text-green-600">Vous avez un accès total sans restriction</p>
                            </div>
                        )}
                        
                        {/* Opening Cash (optional) */}
                        <div>
                            <label className="text-sm font-medium mb-2 block">Fond de caisse (optionnel)</label>
                            <div className="relative">
                                <Input
                                    type="number"
                                    inputMode="numeric"
                                    placeholder="0"
                                    value={openingCash}
                                    onChange={(e) => setOpeningCash(e.target.value)}
                                    className="h-12 pr-12"
                                    data-testid="opening-cash-input"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                                    FCFA
                                </span>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setOpenSessionDialog(false);
                                setPinCode('');
                                setOpeningCash('0');
                            }} 
                            className="flex-1 h-12"
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleOpenMySession} 
                            disabled={(!isAdmin && (!pinCode || pinCode.length < 4)) || openingSession}
                            className="flex-1 h-12 bg-amber-600 hover:bg-amber-700"
                            data-testid="confirm-open-session-btn"
                        >
                            {openingSession ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Unlock className="w-4 h-4 mr-2" />
                            )}
                            Ouvrir
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ServerDashboardPage;
